/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { ArrayExtensions } from './extensions/array-extensions';
import { IProductInventoryInformation } from './product-inventory-information';

/**
 * Cart line inventory validation result interface.
 */
export interface ICartLineInventoryValidationResult {
    /**
     * Define whether the cart lines inventory is valid across multiple cart lines.
     */
    readonly isValid: boolean;

    /**
     * Defines the list of product id with invalid inventory.
     */
    readonly productIdWithErrors: number[];
}

/**
 * Validates inventory across cart lines.
 * @param cartLines - The cart lines.
 * @param productAvailabilities - The product availability collection.
 * @param actionContext - The action context.
 * @param products - The products.
 * @param emailDeliveryModeCode - The emailDeliveryModeCode.
 * @returns True if all product quantity are available, false otherwise.
 */
// eslint-disable-next-line complexity -- existing code.
export function validateCartLinesInventory(
    cartLines: CartLine[],
    productAvailabilities: IProductInventoryInformation[],
    actionContext: IActionContext,
    products?: SimpleProduct[],
    emailDeliveryModeCode?: string | undefined
): ICartLineInventoryValidationResult {
    const cartLineMap = new Map<number, number>();
    const defaultCartLineQuantity = 1;
    if (!actionContext.requestContext.app.config.enableStockCheck) {
        return {
            isValid: true,
            productIdWithErrors: []
        };
    }
    const productIdWithErrors = [];
    let isValid = true;

    if (ArrayExtensions.hasElements(cartLines) && ArrayExtensions.hasElements(productAvailabilities)) {
        // Consolidate products in different cart lines into single entry with the total cart quantity
        for (const cartLine of cartLines) {
            // Skip validation if is an invoice inline.
            if (cartLine.ProductId && !cartLine.IsInvoiceLine && !cartLine.IsGiftCardLine) {
                if (!cartLineMap.has(cartLine.ProductId)) {
                    cartLineMap.set(cartLine.ProductId, cartLine.Quantity ?? defaultCartLineQuantity);
                } else {
                    const cartLineTotal = cartLineMap.get(cartLine.ProductId) ?? 0;
                    cartLineMap.delete(cartLine.ProductId);
                    cartLineMap.set(cartLine.ProductId, cartLineTotal + (cartLine.Quantity ?? defaultCartLineQuantity));
                }
            }
        }

        // Hashing product availability object by product ID.
        const productAvailabilityMap = new Map<number, IProductInventoryInformation>();
        for (const productAvailability of productAvailabilities) {
            if (
                productAvailability.ProductAvailableQuantity.ProductId &&
                !productAvailabilityMap.has(productAvailability.ProductAvailableQuantity.ProductId)
            ) {
                productAvailabilityMap.set(productAvailability.ProductAvailableQuantity.ProductId, productAvailability);
            }
        }

        // Compare total quantity with the available quantity from the inventory API, also validate that the product is available.
        for (const productId of Array.from(cartLineMap.keys())) {
            const cartLineQty = cartLineMap.get(productId) ?? defaultCartLineQuantity;
            const productAvailability = productAvailabilityMap.get(productId);
            const availableQuantity = productAvailability?.ProductAvailableQuantity.AvailableQuantity ?? undefined;

            // If product is non-stocked or cartLine is electronic delivery, then no inventory check
            const cartLine = cartLines.find(line => line.ProductId === productId);
            const cartLineProduct = products?.find(product => product.RecordId === productId);
            let isStockedItem = true;
            if (cartLineProduct) {
                isStockedItem = !!cartLineProduct.Behavior?.IsStockedProduct;
            }
            const isEmailDelivery =
                cartLine?.DeliveryMode && cartLine.DeliveryMode !== '' ? cartLine.DeliveryMode === emailDeliveryModeCode : false;
            const shouldSkipInventoryCheck = !isStockedItem || isEmailDelivery;

            // If product is non-stocked or cartLine is electronic delivery, then no inventory check
            if (shouldSkipInventoryCheck) {
                continue;
            }
            if (!productAvailability?.IsProductAvailable || (availableQuantity && cartLineQty > availableQuantity)) {
                productIdWithErrors.push(productId);
                isValid = false;
            }
        }
    }
    return {
        isValid,
        productIdWithErrors
    };
}
